import { UwBRLocationReview } from 'types/underwriting.type';
import { Location } from 'types/location.type';
import { BR_CONSTANTS } from 'application/Providers/Installation/BuildersRisk/Helpers/BrConstants';
import { getBrTotalPolicyPremiumFromLocations } from '../../../../../../../helpers/brCalculationHelper';

// Class used to model data for display and editing in BR reviews.
// Also used to post a new UW BR Review
export default class BRPropertyModel implements UwBRLocationReview {
    static BuildDefaultBRPropertyModel(location: Location): BRPropertyModel {
        const baseRate = location.baseRate || 0;
        const ret = new BRPropertyModel({
            locationId: location.id,
            softCostMultiplier: BR_CONSTANTS.SOFT_COST_MULTIPLIER,
            lossOfGrossEarningsMultiplier: BR_CONSTANTS.LOSS_OF_GROSS_EARNINGS_MULTIPLIER,
            lossOfRentsMultiplier: BR_CONSTANTS.LOSS_OF_RENTAL_INCOME_MULTIPLIER,
            zone: location.zone,
            baseRate: baseRate,
            finalRate: baseRate,
            technicalFinalRate: baseRate,
            hardCostPremium: ((location.hardCosts || 0) * baseRate) / 100,
            softCostFinalRate: baseRate * BR_CONSTANTS.SOFT_COST_MULTIPLIER,
            softCostPremium: ((location.softCosts || 0) * baseRate * BR_CONSTANTS.SOFT_COST_MULTIPLIER) / 100,
            lossOfGrossEarningsFinalRate: baseRate * BR_CONSTANTS.LOSS_OF_GROSS_EARNINGS_MULTIPLIER,
            lossOfGrossEarningsPremium:
                ((location.lossOfGrossEarnings || 0) * baseRate * BR_CONSTANTS.LOSS_OF_GROSS_EARNINGS_MULTIPLIER) / 100,
            lossOfRentalIncomeFinalRate: baseRate * BR_CONSTANTS.LOSS_OF_RENTAL_INCOME_MULTIPLIER,
            lossOfRentalIncomePremium:
                ((location.lossOfRentalIncome || 0) * baseRate * BR_CONSTANTS.LOSS_OF_RENTAL_INCOME_MULTIPLIER) / 100,
            triaPercentage: 0,
            hasEnr400Contractor: false,
            isWaterDamageCovered: true,
            inspectionsRequiredCount: 0,
            totalPremium: 0,
        });

        ret.programPremium = getBrTotalPolicyPremiumFromLocations([ret]);
        return ret;
    }

    locationId?: number;
    locationName?: string;
    locationZone?: string;
    brLocationReviewId?: number;
    totalContractValue?: number;
    projectTypeCode?: string;
    catastropheLimitType?: string;
    brCatastropheLimit?: number;
    delayInCompletionCatastropheLimit?: number;
    combinedCatastropheLimit?: number;
    additionalDebrisRemovalExpenses?: number;
    emergencyRemoval?: number;
    fraudAndDeceit?: number;
    limitedFungusCoverage?: number;
    waterborneProperty?: number;
    blueprintsAndConstructionDocuments?: number;
    claimPreparationExpense?: number;
    constructionTrailers?: number;
    expeditingExpenses?: number;
    expenseToReErectScaffolding?: number;
    fireDepartmentServiceCharges?: number;
    ordinanceOrLawUndamagedParts?: number;
    ordinanceOrLawIncreasedCost?: number;
    personalProperty?: number;
    pollutantCleanupAndRemoval?: number;
    rechargeOfFireExtinguishers?: number;
    rewards?: number;
    sewerBackup?: number;
    temporaryStorageLocations?: number;
    transit?: number;
    treesShrubsAndPlants?: number;
    deductible?: number;
    permissionToOccupyDays?: number;
    periodOfIndemnity?: number;
    waitingPeriod?: number;
    softCost30DayLimitAmount?: number;
    hasSoftCost30DayLimit?: boolean;
    rentalIncome30DayLimitAmount?: number;
    hasRentalIncome30DayLimit?: boolean;
    income30DayLimitAmount?: number;
    hasIncome30DayLimit?: boolean;
    zone?: string;
    baseRate?: number;
    modeled?: number;
    additionalCatExposure?: number;
    renovationModifier?: number;
    midTermPlacement?: number;
    totalRateAdditions?: number;
    deductibleStructure?: number;
    contractorQuality?: number;
    siteSecurity?: number;
    leadCarrier?: number;
    constructionModifier?: number;
    totalRateAdjustment?: number;
    softCostMultiplier?: number;
    lossOfRentsMultiplier?: number;
    lossOfGrossEarningsMultiplier?: number;
    finalRate?: number;
    hardCostPremium?: number;
    softCostFinalRate?: number;
    softCostPremium?: number;
    lossOfRentalIncomeFinalRate?: number;
    lossOfRentalIncomePremium?: number;
    lossOfGrossEarningsFinalRate?: number;
    lossOfGrossEarningsPremium?: number;
    hasTria?: boolean;
    triaPercentage?: number;
    totalPremium?: number;
    hasEnr400Contractor?: boolean;
    windstormDeductible?: string;
    namedWindstormDeductible?: string;
    earthMovementDeductible?: string;
    floodDeductible?: string;
    isWaterDamageCovered?: boolean;
    waterDamageSublimit?: number;
    isEarthMovementCovered?: boolean;
    earthMovementSublimit?: number;
    isFloodCovered?: boolean;
    floodSublimit?: number;
    isWindstormCovered?: boolean;
    isNamedWindstormCovered?: boolean;
    windstormSublimit?: number;
    namedWindstormSublimit?: number;
    waterDamageDeductible?: string;
    hasWaterDamageSublimit?: boolean;
    hasEarthMovementSublimit?: boolean;
    hasFloodSublimit?: boolean;
    hasWindstormSublimit?: boolean;
    hasNamedWindstormSublimit?: boolean;
    underwriterAdjustedPremium?: number;
    underwriterAdjustmentFactor?: number;
    technicalFinalRate?: number;
    automaticExtensionPeriod?: string;
    automaticExtensionRate?: number;
    programPremium?: number;
    inspectionsRequiredCount?: number;

    constructor({
        locationId,
        brLocationReviewId,
        projectTypeCode,
        catastropheLimitType,
        brCatastropheLimit,
        delayInCompletionCatastropheLimit,
        combinedCatastropheLimit,
        additionalDebrisRemovalExpenses,
        emergencyRemoval,
        fraudAndDeceit,
        limitedFungusCoverage,
        waterborneProperty,
        blueprintsAndConstructionDocuments,
        claimPreparationExpense,
        constructionTrailers,
        expeditingExpenses,
        expenseToReErectScaffolding,
        fireDepartmentServiceCharges,
        ordinanceOrLawUndamagedParts,
        ordinanceOrLawIncreasedCost,
        personalProperty,
        pollutantCleanupAndRemoval,
        rechargeOfFireExtinguishers,
        rewards,
        sewerBackup,
        temporaryStorageLocations,
        transit,
        treesShrubsAndPlants,
        deductible,
        permissionToOccupyDays,
        periodOfIndemnity,
        waitingPeriod,
        softCost30DayLimitAmount,
        hasSoftCost30DayLimit,
        rentalIncome30DayLimitAmount,
        hasRentalIncome30DayLimit,
        income30DayLimitAmount,
        hasIncome30DayLimit,
        zone,
        baseRate,
        modeled,
        additionalCatExposure,
        renovationModifier,
        midTermPlacement,
        totalRateAdditions,
        deductibleStructure,
        contractorQuality,
        siteSecurity,
        leadCarrier,
        constructionModifier,
        totalRateAdjustment,
        softCostMultiplier,
        lossOfRentsMultiplier,
        lossOfGrossEarningsMultiplier,
        finalRate,
        hardCostPremium,
        softCostFinalRate,
        softCostPremium,
        lossOfRentalIncomeFinalRate,
        lossOfRentalIncomePremium,
        lossOfGrossEarningsFinalRate,
        lossOfGrossEarningsPremium,
        hasTria,
        triaPercentage,
        totalPremium,
        hasEnr400Contractor,
        windstormDeductible,
        namedWindstormDeductible,
        earthMovementDeductible,
        floodDeductible,
        isWaterDamageCovered,
        waterDamageSublimit,
        isEarthMovementCovered,
        earthMovementSublimit,
        isFloodCovered,
        floodSublimit,
        waterDamageDeductible,
        hasWaterDamageSublimit,
        hasEarthMovementSublimit,
        hasFloodSublimit,
        hasWindstormSublimit,
        hasNamedWindstormSublimit,
        underwriterAdjustedPremium,
        underwriterAdjustmentFactor,
        technicalFinalRate,
        automaticExtensionPeriod,
        automaticExtensionRate,
        programPremium,
        inspectionsRequiredCount,
    }: {
        locationId?: number;
        brLocationReviewId?: number;
        projectTypeCode?: string;
        catastropheLimitType?: string;
        brCatastropheLimit?: number;
        delayInCompletionCatastropheLimit?: number;
        combinedCatastropheLimit?: number;
        additionalDebrisRemovalExpenses?: number;
        emergencyRemoval?: number;
        fraudAndDeceit?: number;
        limitedFungusCoverage?: number;
        waterborneProperty?: number;
        blueprintsAndConstructionDocuments?: number;
        claimPreparationExpense?: number;
        constructionTrailers?: number;
        expeditingExpenses?: number;
        expenseToReErectScaffolding?: number;
        fireDepartmentServiceCharges?: number;
        ordinanceOrLawUndamagedParts?: number;
        ordinanceOrLawIncreasedCost?: number;
        personalProperty?: number;
        pollutantCleanupAndRemoval?: number;
        rechargeOfFireExtinguishers?: number;
        rewards?: number;
        sewerBackup?: number;
        temporaryStorageLocations?: number;
        transit?: number;
        treesShrubsAndPlants?: number;
        deductible?: number;
        permissionToOccupyDays?: number;
        periodOfIndemnity?: number;
        waitingPeriod?: number;
        softCost30DayLimitAmount?: number;
        hasSoftCost30DayLimit?: boolean;
        rentalIncome30DayLimitAmount?: number;
        hasRentalIncome30DayLimit?: boolean;
        income30DayLimitAmount?: number;
        hasIncome30DayLimit?: boolean;
        zone?: string;
        baseRate?: number;
        modeled?: number;
        additionalCatExposure?: number;
        renovationModifier?: number;
        midTermPlacement?: number;
        totalRateAdditions?: number;
        deductibleStructure?: number;
        contractorQuality?: number;
        siteSecurity?: number;
        leadCarrier?: number;
        constructionModifier?: number;
        totalRateAdjustment?: number;
        softCostMultiplier?: number;
        lossOfRentsMultiplier?: number;
        lossOfGrossEarningsMultiplier?: number;
        finalRate?: number;
        hardCostPremium?: number;
        softCostFinalRate?: number;
        softCostPremium?: number;
        lossOfRentalIncomeFinalRate?: number;
        lossOfRentalIncomePremium?: number;
        lossOfGrossEarningsFinalRate?: number;
        lossOfGrossEarningsPremium?: number;
        hasTria?: boolean;
        triaPercentage?: number;
        totalPremium?: number;
        hasEnr400Contractor?: boolean;
        windstormDeductible?: string;
        namedWindstormDeductible?: string;
        earthMovementDeductible?: string;
        floodDeductible?: string;
        isWaterDamageCovered?: boolean;
        waterDamageSublimit?: number;
        isEarthMovementCovered?: boolean;
        earthMovementSublimit?: number;
        isFloodCovered?: boolean;
        floodSublimit?: number;
        waterDamageDeductible?: string;
        hasWaterDamageSublimit?: boolean;
        hasEarthMovementSublimit?: boolean;
        hasFloodSublimit?: boolean;
        hasWindstormSublimit?: boolean;
        hasNamedWindstormSublimit?: boolean;
        underwriterAdjustedPremium?: number;
        underwriterAdjustmentFactor?: number;
        technicalFinalRate?: number;
        automaticExtensionPeriod?: string;
        automaticExtensionRate?: number;
        programPremium?: number;
        inspectionsRequiredCount?: number;
    }) {
        this.locationId = locationId;
        this.brLocationReviewId = brLocationReviewId;
        this.projectTypeCode = projectTypeCode ?? 'NEW_CONSTRUCTION';
        this.catastropheLimitType = catastropheLimitType;
        this.brCatastropheLimit = brCatastropheLimit;
        this.delayInCompletionCatastropheLimit = delayInCompletionCatastropheLimit;
        this.combinedCatastropheLimit = combinedCatastropheLimit;
        this.additionalDebrisRemovalExpenses = additionalDebrisRemovalExpenses;
        this.emergencyRemoval = emergencyRemoval;
        this.fraudAndDeceit = fraudAndDeceit;
        this.limitedFungusCoverage = limitedFungusCoverage;
        this.waterborneProperty = waterborneProperty;
        this.blueprintsAndConstructionDocuments = blueprintsAndConstructionDocuments;
        this.claimPreparationExpense = claimPreparationExpense;
        this.constructionTrailers = constructionTrailers;
        this.expeditingExpenses = expeditingExpenses;
        this.expenseToReErectScaffolding = expenseToReErectScaffolding;
        this.fireDepartmentServiceCharges = fireDepartmentServiceCharges;
        this.ordinanceOrLawUndamagedParts = ordinanceOrLawUndamagedParts;
        this.ordinanceOrLawIncreasedCost = ordinanceOrLawIncreasedCost;
        this.personalProperty = personalProperty;
        this.pollutantCleanupAndRemoval = pollutantCleanupAndRemoval;
        this.rechargeOfFireExtinguishers = rechargeOfFireExtinguishers;
        this.rewards = rewards;
        this.sewerBackup = sewerBackup;
        this.temporaryStorageLocations = temporaryStorageLocations;
        this.transit = transit;
        this.treesShrubsAndPlants = treesShrubsAndPlants;
        this.deductible = deductible;
        this.permissionToOccupyDays = permissionToOccupyDays;
        this.periodOfIndemnity = periodOfIndemnity;
        this.waitingPeriod = waitingPeriod;
        this.softCost30DayLimitAmount = softCost30DayLimitAmount;
        this.hasSoftCost30DayLimit = hasSoftCost30DayLimit;
        this.rentalIncome30DayLimitAmount = rentalIncome30DayLimitAmount;
        this.hasRentalIncome30DayLimit = hasRentalIncome30DayLimit;
        this.income30DayLimitAmount = income30DayLimitAmount;
        this.hasIncome30DayLimit = hasIncome30DayLimit;
        this.zone = zone;
        this.baseRate = baseRate;
        this.modeled = modeled;
        this.additionalCatExposure = additionalCatExposure;
        this.renovationModifier = renovationModifier;
        this.midTermPlacement = midTermPlacement;
        this.totalRateAdditions = totalRateAdditions;
        this.deductibleStructure = deductibleStructure;
        this.contractorQuality = contractorQuality;
        this.siteSecurity = siteSecurity;
        this.leadCarrier = leadCarrier;
        this.constructionModifier = constructionModifier;
        this.totalRateAdjustment = totalRateAdjustment;
        this.softCostMultiplier = softCostMultiplier;
        this.lossOfRentsMultiplier = lossOfRentsMultiplier;
        this.lossOfGrossEarningsMultiplier = lossOfGrossEarningsMultiplier;
        this.finalRate = finalRate;
        this.hardCostPremium = hardCostPremium;
        this.softCostFinalRate = softCostFinalRate;
        this.softCostPremium = softCostPremium;
        this.lossOfRentalIncomeFinalRate = lossOfRentalIncomeFinalRate;
        this.lossOfRentalIncomePremium = lossOfRentalIncomePremium;
        this.lossOfGrossEarningsFinalRate = lossOfGrossEarningsFinalRate;
        this.lossOfGrossEarningsPremium = lossOfGrossEarningsPremium;
        this.hasTria = hasTria || (!!triaPercentage && triaPercentage > 0);
        this.triaPercentage = triaPercentage;
        this.totalPremium = totalPremium;
        this.hasEnr400Contractor = hasEnr400Contractor;
        this.windstormDeductible = windstormDeductible;
        this.namedWindstormDeductible = namedWindstormDeductible;
        this.earthMovementDeductible = earthMovementDeductible;
        this.floodDeductible = floodDeductible;
        this.isWaterDamageCovered = isWaterDamageCovered;
        this.waterDamageSublimit = waterDamageSublimit;
        this.isEarthMovementCovered = isEarthMovementCovered;
        this.earthMovementSublimit = earthMovementSublimit;
        this.isFloodCovered = isFloodCovered;
        this.floodSublimit = floodSublimit;
        this.waterDamageDeductible = waterDamageDeductible;
        this.hasWaterDamageSublimit = hasWaterDamageSublimit;
        this.hasEarthMovementSublimit = hasEarthMovementSublimit;
        this.hasFloodSublimit = hasFloodSublimit;
        this.hasWindstormSublimit = hasWindstormSublimit;
        this.hasNamedWindstormSublimit = hasNamedWindstormSublimit;
        this.underwriterAdjustmentFactor = underwriterAdjustmentFactor;
        this.underwriterAdjustedPremium = underwriterAdjustedPremium;
        this.technicalFinalRate = technicalFinalRate;
        this.automaticExtensionPeriod = automaticExtensionPeriod;
        this.automaticExtensionRate = automaticExtensionRate;
        this.programPremium = programPremium;
        this.inspectionsRequiredCount = inspectionsRequiredCount ?? 0;
    }
}
